/**
 * Slash commands for the chat. All responses are shown only to the prompting user.
 */

const API = process.env.REACT_APP_API_URL || '';

export const COMMAND_LIST = [
  { cmd: '/ohjeet', desc: 'Listaa kaikki käytettävissä olevat komennot' },  
  { cmd: '/kuiskaa {käyttäjä} [viesti]', desc: 'Lähetä [viesti], jonka näkee vain {käyttäjä}' },
    { cmd: '/sää {kaupunki}', desc: 'Sää Suomessa (Jyväskylä oletuksena)' },
  { cmd: '/hajatelma', desc: 'Satunnainen lainaus englannista suomeksi' },
  { cmd: '/soi', desc: 'Nyt soitettavan kappaleen tiedot' },
  { cmd: '/varaukset', desc: 'Tulevat tapahtumat' },
  { cmd: '/putsaa', desc: 'Tyhjentää kaikki Pulinan viestit' },

];

/**
 * Returns { cmd, args } if text is a slash command, otherwise null.
 */
export function parseSlashCommand(text) {
  const raw = (text || '').trim();
  if (!raw) return null;
  if (raw.toLowerCase() === 'varaukset') return { cmd: '/varaukset', args: '' };
  const match = raw.match(/^\/(\S+)(?:\s+(.*))?$/);
  if (match) return { cmd: '/' + match[1].toLowerCase(), args: (match[2] || '').trim() };
  return null;
}

/**
 * Check if the given text triggers a slash command.
 */
export function isSlashCommand(text) {
  return parseSlashCommand(text) !== null;
}

/**
 * Execute a slash command and return the response HTML string.
 * @param {string} cmd - e.g. '/ohjeet', '/sää'
 * @param {string} args - optional arguments
 * @param {object} context - { user, currentTrack, upcomingEvents, formatEventDay, getEventStartTime }
 */
export async function executeSlashCommand(cmd, args, context = {}) {
  const { user, currentTrack, upcomingEvents = [], formatEventDay = (d) => d, getEventStartTime } = context;

  switch (cmd) {
    case '/kuiskaa': {
      const argStr = (args || '').trim();
      const spaceIdx = argStr.indexOf(' ');
      const targetUsername = spaceIdx >= 0 ? argStr.slice(0, spaceIdx).trim() : argStr;
      const messageText = (spaceIdx >= 0 ? argStr.slice(spaceIdx).trim() : '') || '(kuiskaus)';
      if (!targetUsername) {
        return 'Käytä: <b>/kuiskaa {käyttäjänimi} [viesti]</b>';
      }
      try {
        const presRes = await fetch(`${API}/api/chat/presence`);
        if (!presRes.ok) throw new Error('Presence check failed');
        const { present = [] } = await presRes.json();
        const target = targetUsername.trim().toLowerCase();
        const alwaysAllowed = target === 'pulinabotti';
        const isPresent =
          alwaysAllowed ||
          present.some((p) => {
            const u = (p.username || '').trim().toLowerCase();
            const d = (p.displayName || '').trim().toLowerCase();
            return u === target || d === target;
          });
        if (!isPresent) {
          return `${targetUsername} ei ole täällä`;
        }
        const whisperMsg = {
          text: messageText.slice(0, 500),
          authorName: user?.displayName || user?.username || '?',
          authorId: user?.id,
          whisperTo: targetUsername.trim(),
        };
        const postRes = await fetch(`${API}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: [whisperMsg] }),
        });
        if (!postRes.ok) throw new Error('Failed to send whisper');
        // No Pulina slash bubble — confirmation is shown only as chat whisper lines.
        return null;
      } catch (e) {
        return `${targetUsername} ei ole täällä`;
      }
    }

    case '/ohjeet': {
      const lines = COMMAND_LIST.map(({ cmd: c, desc }) => `<b>${c}</b> – ${desc}`);
      return lines.join('<br/>');
    }

    case '/sää': {
      const city = args || 'Jyväskylä';
      try {
        const res = await fetch(`${API}/api/weather?city=${encodeURIComponent(city)}`);
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        return data.html || data.error || 'Sää ei saatavilla.';
      } catch (e) {
        return 'Sään haku epäonnistui.';
      }
    }

    case '/hajatelma': {
      try {
        const res = await fetch(`${API}/api/quote`);
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        return data.html || data.error || 'Lainaus ei saatavilla.';
      } catch (e) {
        return 'Lainauksen haku epäonnistui.';
      }
    }

    case '/soi': {
      if (!currentTrack) return '<b>Nyt soi</b> – Ei soi mitään.';
      const t = currentTrack;
      const artist = t.user?.name || '—';
      const title = t.title || '—';
      const genre = t.genre || '—';
      const duration = t.duration ? `${Math.floor(t.duration / 60)}:${String(t.duration % 60).padStart(2, '0')}` : '—';
      const parts = [
        `<b>Nyt soi</b> ${title} — ${artist} `,
        `[Genre: ${genre}] `,
        `| Kesto: ${duration}`,
      ];
      if (t.permalink) {
        parts.push(`<a href="https://audius.co${t.permalink.startsWith('/') ? '' : '/'}${t.permalink}" target="_blank" rel="noopener noreferrer">Kuuntele Audiuksessa</a>`);
      }
      return parts.join('<br/>');
    }

    case '/varaukset': {
      const upcoming = (upcomingEvents || []).filter((e) => getEventStartTime && getEventStartTime(e).getTime() > Date.now());
      if (upcoming.length === 0) return 'Ei tulevia tapahtumia.';
      const lines = upcoming.slice(0, 10).map((ev) => {
        const { base, label } = formatEventDay(ev.eventDate);
        const time = ev.time || '--:--';
        const labelStr = label ? ` (${label})` : '';
        return `<b>${ev.title}</b> – ${base} klo ${time}${labelStr}`;
      });
      return lines.join('<br/>');
    }

    default:
      return 'Tuntematon komento. Kirjoita <b>/ohjeet</b> nähdäksesi komennot.';
  }
}
